import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ReactElement } from 'react';
import { Input } from '@/shared/components/ui/Input';
import { Button } from '@/shared/components/ui/Button';
import { Tabs } from '@/shared/components/ui/Tabs';
import type { TabItem } from '@/shared/components/ui/Tabs';
import { Icon } from '@/shared/icons';
import type { ChannelConnectionDetails } from '@troia-v3/types';
import { useChannelConnectionDetails } from '../hooks/useChannelConnectionDetails';
import { useReconnectChannel } from '../hooks/useReconnectChannel';
import { useMetaEmbeddedSignup } from '../hooks/useMetaEmbeddedSignup';
import { useFacebookEmbeddedSignup } from '../hooks/useFacebookEmbeddedSignup';
import { useInstagramEmbeddedSignup } from '../hooks/useInstagramEmbeddedSignup';

// CSS variables override to force light theme inside modal (CLAUDE.md rule)
const LIGHT_THEME_VARS: React.CSSProperties = {
  '--theme-text': '#1e293b',
  '--theme-muted-text': '#6b7280',
  '--theme-input-background': '#ffffff',
  '--theme-input-text': '#1e293b',
  '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
  '--theme-input-focus': '#8b5cf6',
  '--theme-error-color': '#ef4444',
  '--theme-warning-color': '#d97706',
  '--theme-card-background': '#ffffff',
  '--theme-card-text': '#334155',
  '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-card-muted': '#94a3b8',
  '--theme-button-primary': '#8b5cf6',
  '--theme-button-primary-text': '#ffffff',
  '--theme-button-primary-hover': '#7c3aed',
  '--theme-button-secondary': '#f8fafc',
  '--theme-button-secondary-text': '#374151',
  '--theme-button-secondary-hover': '#f1f5f9',
  '--theme-primary': '#8b5cf6',
  '--theme-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-component-background': '#ffffff',
} as React.CSSProperties;

const PROVIDER_LABEL: Record<string, string> = {
  'whatsapp-business': 'WhatsApp Business',
  'facebook-messenger': 'Facebook Messenger',
  'instagram-messaging': 'Instagram',
};

// ---------- Props ----------

export interface ReconnectOfficialModalProps {
  channelId: string;
  providerId: string;
  isOpen: boolean;
  onClose: () => void;
}

// ---------- ReadOnlyField helper ----------

interface ReadOnlyFieldProps {
  label: string;
  value: string;
  testid: string;
  copyable?: boolean;
}

function ReadOnlyField({ label, value, testid, copyable }: ReadOnlyFieldProps): ReactElement {
  return (
    <div>
      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--theme-card-text)' }}>
        {label}
      </label>
      <div className="flex items-center gap-2">
        <Input value={value} readOnly disabled data-testid={testid} />
        {copyable && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => { void navigator.clipboard.writeText(value); }}
            data-testid={`${testid}-copy`}
            title="Copiar"
          >
            <Icon name="copy" className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------- Manual Tab ----------

interface ManualTabProps {
  providerId: string;
  details: ChannelConnectionDetails | undefined;
  accessToken: string;
  onAccessTokenChange: (v: string) => void;
}

function ManualTab({ details, accessToken, onAccessTokenChange }: ManualTabProps): ReactElement {
  return (
    <div className="space-y-3 pt-4">
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--theme-card-text)' }}>
          Token de acesso
        </label>
        <Input
          value={accessToken}
          onChange={(e) => onAccessTokenChange(e.target.value)}
          placeholder="EAAxxxxxxxxxxxxxxx"
          data-testid="reconnect-official-token"
        />
      </div>

      {/* Provider config fields — read-only */}
      {details?.whatsappBusinessAccountId && (
        <ReadOnlyField
          label="Business Account ID"
          value={details.whatsappBusinessAccountId}
          testid="reconnect-official-waba"
        />
      )}
      {details?.phoneNumberId && (
        <ReadOnlyField
          label="Phone Number ID"
          value={details.phoneNumberId}
          testid="reconnect-official-phone"
        />
      )}
      {details?.pageId && (
        <ReadOnlyField
          label="Page ID"
          value={details.pageId}
          testid="reconnect-official-page"
        />
      )}
      {details?.instagramBusinessAccountId && (
        <ReadOnlyField
          label="Instagram Account ID"
          value={details.instagramBusinessAccountId}
          testid="reconnect-official-ig"
        />
      )}

      {/* Webhook URL — read-only + copyable */}
      {details?.webhookUrl && (
        <ReadOnlyField
          label="URL do Webhook"
          value={details.webhookUrl}
          testid="reconnect-official-webhook"
          copyable
        />
      )}
    </div>
  );
}

// ---------- Facebook Tab ----------

interface FacebookTabProps {
  providerId: string;
  details: ChannelConnectionDetails | undefined;
  onToken: (token: string) => void;
}

function FacebookTab({ providerId, details, onToken }: FacebookTabProps): ReactElement {
  const meta = useMetaEmbeddedSignup();
  const fb = useFacebookEmbeddedSignup();
  const ig = useInstagramEmbeddedSignup();

  // Watch each hook's result and fire onToken when a new token arrives.
  // The hooks are event-driven (startEmbeddedSignup → async FB.login flow → result set in state).
  // We cannot await the token directly, so we use useEffect to react to state changes.
  useEffect(() => {
    if (meta.result?.accessToken && providerId === 'whatsapp-business') {
      onToken(meta.result.accessToken);
    }
  }, [meta.result, providerId, onToken]);

  useEffect(() => {
    if (fb.result?.accessToken && providerId === 'facebook-messenger') {
      onToken(fb.result.accessToken);
    }
  }, [fb.result, providerId, onToken]);

  useEffect(() => {
    if (ig.result?.accessToken && providerId === 'instagram-messaging') {
      onToken(ig.result.accessToken);
    }
  }, [ig.result, providerId, onToken]);

  const isLoading = meta.isLoading || fb.isLoading || ig.isLoading;
  const hookError = meta.error ?? fb.error ?? ig.error ?? null;

  const handleRenew = () => {
    if (providerId === 'whatsapp-business') {
      void meta.startEmbeddedSignup();
    } else if (providerId === 'facebook-messenger') {
      void fb.startEmbeddedSignup();
    } else if (providerId === 'instagram-messaging') {
      void ig.startEmbeddedSignup();
    }
  };

  const accountHint =
    details?.whatsappBusinessAccountId ??
    details?.pageId ??
    details?.instagramBusinessAccountId ??
    'atual';

  return (
    <div className="space-y-3 pt-4">
      <p className="text-sm" style={{ color: 'var(--theme-card-muted)' }}>
        Renove o token de acesso entrando com o Facebook. A conta conectada permanece a mesma
        ({accountHint}).
      </p>

      {hookError && (
        <p className="text-sm" style={{ color: 'var(--theme-error-color)' }} data-testid="reconnect-official-fb-error">
          {hookError}
        </p>
      )}

      <Button
        onClick={handleRenew}
        disabled={isLoading}
        loading={isLoading}
        data-testid="reconnect-official-fb-start"
      >
        <Icon name="facebook" className="w-4 h-4 mr-2" />
        {isLoading ? 'Aguardando Facebook...' : 'Renovar com Facebook'}
      </Button>
    </div>
  );
}

// ---------- Main Modal ----------

export const ReconnectOfficialModal: React.FC<ReconnectOfficialModalProps> = ({
  channelId,
  providerId,
  isOpen,
  onClose,
}) => {
  // Hooks must run unconditionally (before any early return).
  // useChannelConnectionDetails is gated by `enabled: isOpen` so no fetch when closed.
  const { data: details, isLoading: detailsLoading } = useChannelConnectionDetails(channelId, isOpen);
  const reconnect = useReconnectChannel();

  const [activeTab, setActiveTab] = useState<string>('manual');
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // Pre-fill token when connection details arrive
  useEffect(() => {
    if (details?.accessToken) {
      setAccessToken(details.accessToken);
    }
  }, [details?.accessToken]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('manual');
      setAccessToken('');
      setError(null);
      setWarning(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setError(null);
    setWarning(null);
    try {
      const result = await reconnect.mutateAsync({
        id: channelId,
        body: { credentials: { accessToken } },
      });
      if (result.webhookWarning) {
        setWarning(
          `Token salvo, mas o webhook não pôde ser reassinado: ${result.webhookWarning}`,
        );
        return;
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha ao reconectar');
    }
  };

  // When Facebook tab returns a token, switch to manual tab so user can confirm before saving
  const handleFacebookToken = (token: string) => {
    setAccessToken(token);
    setActiveTab('manual');
  };

  const tabs: TabItem[] = [
    {
      id: 'facebook',
      label: 'Login com Facebook',
      content: (
        <FacebookTab
          providerId={providerId}
          details={details}
          onToken={handleFacebookToken}
        />
      ),
    },
    {
      id: 'manual',
      label: 'Credenciais manuais',
      content: detailsLoading ? (
        <div
          className="py-8 text-center"
          style={{ color: '#64748b' }}
          data-testid="reconnect-official-loading"
        >
          Carregando dados do canal...
        </div>
      ) : (
        <ManualTab
          providerId={providerId}
          details={details}
          accessToken={accessToken}
          onAccessTokenChange={setAccessToken}
        />
      ),
    },
  ];

  return createPortal((
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      /* form: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
      data-testid="reconnect-official-modal"
    >
      <div
        className="relative w-full max-w-lg rounded-lg shadow-xl"
        style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          ...LIGHT_THEME_VARS,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0"
          style={{ borderBottom: '1px solid #e5e7eb' }}
        >
          <h2 className="text-xl font-semibold" style={{ color: '#1e293b' }}>
            Reconectar {PROVIDER_LABEL[providerId] ?? 'Canal'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: '#64748b' }}
            data-testid="reconnect-official-close"
          >
            <Icon name="x" className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs + content (scrollable) */}
        <div className="flex-1 overflow-y-auto flex flex-col px-6 pb-4" data-testid="reconnect-official-tabs">
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            variant="underline"
            fullWidth
          />
        </div>

        {/* Feedback */}
        <div className="px-6 flex-shrink-0">
          {error && (
            <p
              className="mt-3 text-sm"
              style={{ color: 'var(--theme-error-color)' }}
              data-testid="reconnect-official-error"
            >
              {error}
            </p>
          )}
          {warning && (
            <p
              className="mt-3 text-sm"
              style={{ color: 'var(--theme-warning-color)' }}
              data-testid="reconnect-official-warning"
            >
              {warning}
            </p>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex justify-end gap-2 px-6 py-4 flex-shrink-0"
          style={{ borderTop: '1px solid #e5e7eb' }}
        >
          <Button
            variant="secondary"
            onClick={onClose}
            data-testid="reconnect-official-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => { void handleSave(); }}
            disabled={reconnect.isPending || !accessToken.trim()}
            loading={reconnect.isPending}
            data-testid="reconnect-official-save"
          >
            {reconnect.isPending ? 'Salvando...' : 'Salvar reconexão'}
          </Button>
        </div>
      </div>
    </div>
  ), document.body);
};

export default ReconnectOfficialModal;
